import { BiEnvelope, BiPhone, BiUser } from "react-icons/bi";
import useStaff from "../../hooks/useStaff";
import useBranches from "../../hooks/useBranches";

const StaffCard = ({ member }) => (
  <div className="flex flex-col items-center text-center p-5 bg-white dark:bg-card-dark border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200">
    {member.photo ? (
      <img
        src={member.photo}
        alt={member.name}
        className="w-20 h-20 rounded-full object-cover border-2 border-primary mb-3"
        loading="lazy"
      />
    ) : (
      <div className="w-20 h-20 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center mb-3">
        <BiUser className="text-primary text-3xl" />
      </div>
    )}
    <h4 className="font-bold text-gray-800 dark:text-white text-base leading-tight">{member.name}</h4>
    {member.title && (
      <p className="text-sm text-primary font-medium mt-0.5">{member.title}</p>
    )}
    <div className="mt-3 space-y-1 text-sm">
      {member.phone && (
        <p className="flex items-center gap-1 text-muted justify-center">
          <BiPhone className="shrink-0" />
          <a href={`tel:${member.phone.replace(/\s/g, "")}`} className="hover:text-primary hover:underline">
            {member.phone}
          </a>
        </p>
      )}
      {member.email && (
        <p className="flex items-center gap-1 text-muted justify-center">
          <BiEnvelope className="shrink-0" />
          <a href={`mailto:${member.email}`} className="hover:text-primary hover:underline normal-case break-all">
            {member.email}
          </a>
        </p>
      )}
    </div>
  </div>
);

const StaffSkeleton = () => (
  <div className="h-56 rounded-xl bg-gray-100 dark:bg-dark-light animate-pulse" />
);

const StaffSection = () => {
  const { data: staff, isLoading: staffLoading } = useStaff();
  const { data: branches, isLoading: branchLoading } = useBranches();

  if (staffLoading || branchLoading) {
    return (
      <section className="pt-10 pb-12">
        <div className="text-center mb-8">
          <span className="sub-heading mx-auto">Ekibimiz</span>
          <h2 className="heading">Çalışanlarımız</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <StaffSkeleton key={i} />)}
        </div>
      </section>
    );
  }

  if (!staff || staff.length === 0) return null;

  // Şubeye göre grupla
  const branchMap = Object.fromEntries(
    (branches || []).map((b) => [b.dbId ?? b.id, b.name])
  );

  const grouped = staff.reduce((acc, member) => {
    const branchName = branchMap[member.pointId] || "Diğer";
    if (!acc[branchName]) acc[branchName] = [];
    acc[branchName].push(member);
    return acc;
  }, {});

  return (
    <section
      id="ekibimiz"
      className="scroll-mt-28 pt-12 pb-16 border-t border-gray-200 dark:border-gray-700"
      aria-label="Çalışanlar"
    >
      <div className="text-center mb-10">
        <span className="sub-heading mx-auto">Ekibimiz</span>
        <h2 className="heading">Çalışanlarımız</h2>
      </div>

      {Object.entries(grouped).map(([branchName, members]) => (
        <div key={branchName} className="mb-12">
          <h3 className="text-lg font-semibold text-[#674921] dark:text-primary mb-5 pb-2 border-b border-gray-200 dark:border-gray-700">
            {branchName} Şubesi
          </h3>
          <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {members.map((member) => (
              <StaffCard key={member.id} member={member} />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
};

export default StaffSection;
